/**
 * Market data source — fetches stock indices, commodities, and crypto prices.
 * Uses CoinGecko free tier for crypto. Finnhub for stocks if API key provided.
 * Falls back to mock/stale data when APIs are unreachable.
 */

import type { Source, SourceMeta, ConfigField, SourceStatus, NormalizedEvent, Severity } from '../base-source';
import { getApiBase } from '@/app/api-router';

const SOURCE_ID = 'market';

/** Market asset categories. */
type AssetCategory = 'index' | 'commodity' | 'crypto' | 'forex';

interface MarketQuote {
  symbol: string;
  name: string;
  category: AssetCategory;
  price: number;
  change24h: number;       // Percentage change
  volume24h?: number;
  high24h?: number;
  low24h?: number;
  sparkline?: number[];    // 7-day hourly prices for mini chart
  updatedAt: string;
}

/** CoinGecko /coins/markets response item. */
interface CoinGeckoMarket {
  id: string;
  symbol: string;
  name: string;
  current_price: number;
  price_change_percentage_24h: number;
  total_volume: number;
  high_24h: number;
  low_24h: number;
  sparkline_in_7d?: { price: number[] };
  last_updated: string;
}

/** Finnhub quote response. */
interface FinnhubQuote {
  c: number;  // Current price
  d: number;  // Change
  dp: number; // Percent change
  h: number;  // High
  l: number;  // Low
  o: number;  // Open
  pc: number; // Previous close
  t: number;  // Timestamp
}

/** Well-known stock indices to track. */
const STOCK_INDICES = [
  { symbol: 'SPY', name: 'S&P 500', finnhubSymbol: 'SPY' },
  { symbol: 'QQQ', name: 'NASDAQ 100', finnhubSymbol: 'QQQ' },
  { symbol: 'DIA', name: 'Dow Jones', finnhubSymbol: 'DIA' },
  { symbol: 'IWM', name: 'Russell 2000', finnhubSymbol: 'IWM' },
  { symbol: 'EFA', name: 'MSCI EAFE', finnhubSymbol: 'EFA' },
  { symbol: 'VWO', name: 'Emerging Markets', finnhubSymbol: 'VWO' },
];

/** Commodity ETFs to track (when no dedicated commodity API). */
const COMMODITY_ETFS = [
  { symbol: 'GLD', name: 'Gold', finnhubSymbol: 'GLD' },
  { symbol: 'USO', name: 'Crude Oil', finnhubSymbol: 'USO' },
  { symbol: 'SLV', name: 'Silver', finnhubSymbol: 'SLV' },
  { symbol: 'UNG', name: 'Natural Gas', finnhubSymbol: 'UNG' },
];

/** Top crypto assets to track via CoinGecko. */
const CRYPTO_IDS = ['bitcoin', 'ethereum', 'solana', 'ripple', 'cardano', 'dogecoin'];

function changeToSeverity(changePct: number): Severity {
  const abs = Math.abs(changePct);
  if (abs > 10) return 'critical';
  if (abs > 5) return 'high';
  if (abs > 2) return 'medium';
  return 'low';
}

let status: SourceStatus = {
  state: 'initializing',
  eventCount: 0,
};

/** In-memory quote cache for panel consumption. */
let latestQuotes: MarketQuote[] = [];

/** Get latest market quotes (consumed by market-panel). */
export function getLatestMarketQuotes(): MarketQuote[] {
  return latestQuotes;
}

export const marketSource: Source = {
  id: SOURCE_ID,

  meta: {
    name: 'Market Data',
    description: 'Stock indices, commodities, crypto prices (CoinGecko + Finnhub)',
    icon: 'trending-up',
    category: 'finance',
    updateInterval: 300_000, // 5 min
    requiresApiKey: false,   // CoinGecko free tier works without key
    dataLicense: 'CoinGecko Terms / Finnhub Terms',
    builtIn: true,
  } satisfies SourceMeta,

  configSchema: [
    {
      key: 'finnhub_api_key',
      label: 'Finnhub API Key (optional)',
      type: 'password',
      required: false,
      helpText: 'Free tier: 60 calls/min. Get one at https://finnhub.io',
    },
  ] as ConfigField[],

  async initialize(): Promise<void> {
    status = { state: 'initializing', eventCount: 0 };
  },

  async start(): Promise<void> {
    status.state = 'active';
    console.log('[Sentinel] Market data source started');
  },

  async stop(): Promise<void> {
    status.state = 'disabled';
  },

  async fetch(): Promise<NormalizedEvent[]> {
    try {
      const quotes: MarketQuote[] = [];
      const events: NormalizedEvent[] = [];

      // 1. Fetch crypto from CoinGecko (free, no key needed)
      try {
        const cryptoQuotes = await fetchCryptoQuotes();
        quotes.push(...cryptoQuotes);
      } catch (err) {
        console.warn('[Sentinel] CoinGecko fetch failed:', err);
      }

      // 2. Fetch stock indices + commodities via proxy (Finnhub if key available)
      try {
        const stockQuotes = await fetchStockQuotes();
        quotes.push(...stockQuotes);
      } catch (err) {
        console.warn('[Sentinel] Stock quote fetch failed:', err);
      }

      // Update cache
      latestQuotes = quotes;

      // Convert notable moves to NormalizedEvents
      for (const q of quotes) {
        // Only emit events for significant moves — thresholds vary by asset class
        const threshold = q.category === 'crypto' ? 8 : q.category === 'commodity' ? 5 : 3;
        if (Math.abs(q.change24h) < threshold) continue;

        const direction = q.change24h > 0 ? 'up' : 'down';
        const event: NormalizedEvent = {
          id: `market-${q.symbol}-${Date.now()}`,
          source: SOURCE_ID,
          timestamp: q.updatedAt,
          type: 'economic',
          title: `${q.name} (${q.symbol}) ${direction} ${Math.abs(q.change24h).toFixed(1)}% — $${formatPrice(q.price)}`,
          severity: changeToSeverity(q.change24h),
          tags: ['market', q.category, q.symbol.toLowerCase()],
          raw: q,
        };

        events.push(event);
      }

      status.state = 'active';
      status.lastFetch = new Date().toISOString();
      status.eventCount = events.length;
      return events;
    } catch (err) {
      status.state = 'error';
      status.lastError = err instanceof Error ? err.message : String(err);
      throw err;
    }
  },

  getStatus(): SourceStatus {
    return { ...status };
  },
};

/** Fetch crypto quotes from CoinGecko via proxy. */
async function fetchCryptoQuotes(): Promise<MarketQuote[]> {
  const ids = CRYPTO_IDS.join(',');
  const url = `${getApiBase()}/api/data/market?provider=coingecko&ids=${ids}&vs_currency=usd&sparkline=true`;

  const response = await fetch(url, { signal: AbortSignal.timeout(15_000) });
  if (!response.ok) {
    throw new Error(`CoinGecko proxy returned ${response.status}`);
  }

  const data: CoinGeckoMarket[] = await response.json();
  if (!Array.isArray(data)) return [];

  return data.map((coin): MarketQuote => ({
    symbol: coin.symbol.toUpperCase(),
    name: coin.name,
    category: 'crypto',
    price: coin.current_price,
    change24h: coin.price_change_percentage_24h ?? 0,
    volume24h: coin.total_volume,
    high24h: coin.high_24h,
    low24h: coin.low_24h,
    sparkline: coin.sparkline_in_7d?.price?.slice(-168), // Last 7 days hourly
    updatedAt: coin.last_updated || new Date().toISOString(),
  }));
}

/** Fetch stock/commodity quotes via proxy (Finnhub or fallback). */
async function fetchStockQuotes(): Promise<MarketQuote[]> {
  const allSymbols = [
    ...STOCK_INDICES.map(s => ({ ...s, category: 'index' as AssetCategory })),
    ...COMMODITY_ETFS.map(s => ({ ...s, category: 'commodity' as AssetCategory })),
  ];

  const symbols = allSymbols.map(s => s.finnhubSymbol).join(',');
  const url = `${getApiBase()}/api/data/market?provider=finnhub&symbols=${symbols}`;

  const response = await fetch(url, { signal: AbortSignal.timeout(15_000) });
  if (!response.ok) {
    throw new Error(`Finnhub proxy returned ${response.status}`);
  }

  const data: Record<string, FinnhubQuote> = await response.json();
  const quotes: MarketQuote[] = [];

  for (const sym of allSymbols) {
    const q = data[sym.finnhubSymbol];
    if (!q || q.c === 0) continue;

    quotes.push({
      symbol: sym.symbol,
      name: sym.name,
      category: sym.category,
      price: q.c,
      change24h: q.dp ?? 0,
      high24h: q.h,
      low24h: q.l,
      updatedAt: q.t ? new Date(q.t * 1000).toISOString() : new Date().toISOString(),
    });
  }

  return quotes;
}

/** Format price with appropriate decimal places. */
function formatPrice(price: number): string {
  if (price >= 1000) return price.toLocaleString('en-US', { maximumFractionDigits: 0 });
  if (price >= 1) return price.toFixed(2);
  return price.toFixed(4);
}
